import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  FileText, 
  Save, 
  Eye, 
  ArrowLeft,
  RefreshCcw,
  Check,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const ContentManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedPage, setSelectedPage] = useState('home');
  const [content, setContent] = useState({});
  const [changes, setChanges] = useState({});
  const [previewMode, setPreviewMode] = useState(false);

  const pages = [
    { id: 'home', name: 'Home Page', icon: '🏠' },
    { id: 'about', name: 'About Page', icon: 'ℹ️' },
    { id: 'services', name: 'Services Page', icon: '🛠️' },
    { id: 'community', name: 'Community Page', icon: '👥' },
    { id: 'contact', name: 'Contact Page', icon: '📧' },
    { id: 'support', name: 'Support Page', icon: '💰' },
    { id: 'footer', name: 'Footer', icon: '⬇️' }
  ];

  useEffect(() => {
    verifyAuth();
    fetchContent();
  }, []);

  const verifyAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/verify`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
    } catch (err) {
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
    }
  };

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/content`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      
      // Organize content by page
      const organizedContent = {};
      response.data.content.forEach(item => {
        if (!organizedContent[item.page]) {
          organizedContent[item.page] = {};
        }
        if (!organizedContent[item.page][item.section]) {
          organizedContent[item.page][item.section] = {};
        }
        organizedContent[item.page][item.section][item.key] = item.value;
      });
      
      setContent(organizedContent);
    } catch (err) {
      console.error('Error fetching content:', err);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (section, key, value) => {
    const changeKey = `${selectedPage}.${section}.${key}`;
    setChanges(prev => ({
      ...prev,
      [changeKey]: { page: selectedPage, section, key, value }
    }));
  };

  const handleSaveAll = async () => {
    if (Object.keys(changes).length === 0) {
      toast.info('No changes to save');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const updates = Object.values(changes);

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/content/bulk-update`,
        { updates },
        { headers: { 'Authorization': `Bearer ${token}` }}
      );

      toast.success(`${updates.length} changes published successfully!`);
      setChanges({});
      fetchContent(); // Refresh content
    } catch (err) {
      console.error('Error saving content:', err);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleRevertAll = () => {
    setChanges({});
    toast.info('All changes reverted');
  };

  const getCurrentValue = (section, key) => {
    const changeKey = `${selectedPage}.${section}.${key}`;
    if (changes[changeKey]) {
      return changes[changeKey].value;
    }
    return content[selectedPage]?.[section]?.[key] || '';
  };

  const hasChanges = Object.keys(changes).length > 0;

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-content" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
          <p>Loading content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <SEO title="Content Management - Admin" description="Manage website content" />

      {/* Header */}
      <div className="admin-header">
        <div className="container">
          <div className="admin-header-content">
            <div>
              <Button 
                onClick={() => navigate('/admin')} 
                variant="outline"
                style={{ marginBottom: '1rem', background: 'white' }}
              >
                <ArrowLeft size={20} />
                Back to Dashboard
              </Button>
              <h1 className="admin-title">Content Management</h1>
              <p className="admin-subtitle">Edit website content with live preview</p>
            </div>
            <div className="admin-header-actions">
              {hasChanges && (
                <>
                  <Button 
                    onClick={handleRevertAll}
                    variant="outline"
                    style={{ background: 'white' }}
                  >
                    <RefreshCcw size={20} />
                    Revert All
                  </Button>
                  <Button 
                    onClick={handleSaveAll}
                    disabled={saving}
                    className="btn-primary-large"
                  >
                    <Save size={20} />
                    {saving ? 'Publishing...' : `Publish ${Object.keys(changes).length} Changes`}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="admin-content">
        <div className="container">
          {/* Page Selector */}
          <div className="page-selector-grid">
            {pages.map(page => (
              <button
                key={page.id}
                onClick={() => setSelectedPage(page.id)}
                className={`page-selector-btn ${selectedPage === page.id ? 'active' : ''}`}
              >
                <span className="page-icon">{page.icon}</span>
                <span>{page.name}</span>
              </button>
            ))}
          </div>

          {/* Content Editor */}
          <div className="content-editor-container">
            <div className="content-editor-header">
              <h2>
                <FileText size={24} />
                Editing: {pages.find(p => p.id === selectedPage)?.name}
              </h2>
              {hasChanges && (
                <div className="changes-indicator">
                  <AlertCircle size={18} />
                  {Object.keys(changes).length} unsaved changes
                </div>
              )}
            </div>

            {/* Dynamic content sections */}
            <div className="content-sections">
              <div className="content-editor-main">
                {content[selectedPage] && Object.keys(content[selectedPage]).length > 0 ? (
                  Object.entries(content[selectedPage]).map(([section, fields]) => (
                    <div key={section} className="content-section-card">
                      <h3>{section.charAt(0).toUpperCase() + section.slice(1).replace(/_/g, ' ')}</h3>
                      {Object.entries(fields).map(([key, value]) => (
                        <div key={key} className="form-group">
                          <label>{key.replace(/_/g, ' ').charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}</label>
                          {value && value.length > 100 ? (
                            <Textarea
                              placeholder={`Enter ${key.replace(/_/g, ' ')}`}
                              defaultValue={getCurrentValue(section, key)}
                              onChange={(e) => handleContentChange(section, key, e.target.value)}
                              rows={4}
                            />
                          ) : (
                            <Input
                              placeholder={`Enter ${key.replace(/_/g, ' ')}`}
                              defaultValue={getCurrentValue(section, key)}
                              onChange={(e) => handleContentChange(section, key, e.target.value)}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  ))
                ) : (
                  <div className="no-content-message">
                    <FileText size={48} color="#e0e0e0" />
                    <p>No editable content found for this page.</p>
                  </div>
                )}
              </div>

              <div className="content-editor-sidebar">
                <div className="content-info-box">
                  <h4>
                    <Eye size={20} />
                    Preview Your Changes
                  </h4>
                  <p>Changes will be visible immediately after you click "Publish". You can revert all changes before publishing.</p>
                  <Button 
                    onClick={() => window.open(`/${selectedPage === 'home' ? '' : selectedPage}`, '_blank')}
                    variant="outline"
                    style={{ marginTop: '1rem', width: '100%' }}
                  >
                    <Eye size={18} />
                    Preview Page
                  </Button>
                </div>

                <div className="content-help-box">
                  <h4>💡 Quick Tips</h4>
                  <ul>
                    <li>Changes are saved in drafts until published</li>
                    <li>Use "Revert All" to undo unsaved changes</li>
                    <li>Preview the page before publishing</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentManagement;
